import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Send, MessageSquare, Bot, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AIAssistant() {
  const { API, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/history`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setMessages(data);
          setSessionId(data[0].session_id);
        }
      }
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg, created_at: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages((prev) => [...prev, { role: "assistant", content: data.response, created_at: new Date().toISOString() }]);
      }
    } catch (e) {
      console.error(e);
      setMessages((prev) => [...prev, { role: "assistant", content: "Unable to process your request. Please try again.", created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div data-testid="ai-assistant-page" className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-white">AI Property Assistant</h2>
        <p className="text-white/40 text-sm font-sans">Powered by GPT-5.2 — Ask about your properties, legal matters, or get advice</p>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 bg-[#11130F] border border-white/10 rounded-sm overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h3 className="font-serif text-lg text-white mb-2">eZamindar Intelligence</h3>
            <p className="text-white/40 text-sm font-sans max-w-sm">
              Ask me about your properties, court cases, legal documentation, valuation queries, or any property-related matter.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {["What is the status of my properties?", "Explain title verification process", "How to handle property disputes?", "Review my portfolio valuation"].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-left bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-white/50 text-xs font-sans hover:border-[#D4AF37]/40 hover:text-white/70 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center shrink-0">
                <Bot className="w-3.5 h-3.5 text-[#D4AF37]" />
              </div>
            )}
            <div className={`max-w-[75%] rounded-sm px-4 py-3 ${
              msg.role === "user"
                ? "bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                : "bg-white/5 border border-white/10"
            }`}>
              <p className="text-white/80 text-sm font-sans whitespace-pre-wrap leading-relaxed">{msg.content}</p>
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                <User className="w-3.5 h-3.5 text-white/60" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm px-4 py-3">
              <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <input
          data-testid="chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your properties, legal matters, or get advice..."
          className="flex-1 bg-[#11130F] border border-white/10 rounded-sm px-4 py-3 text-white text-sm font-sans placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
        />
        <Button
          data-testid="chat-send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm px-4 disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
